import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { FormInstance } from "antd";
import { RefObject } from "react";
import { strings } from "utils/localizedStrings";
import { ManageConfigInjection } from "../../di/ManageConfigInjection";
import { ManageConfigListRequestEntity } from "../../domain/entities/ManageConfigListRequestEntity";
import { ManageConfigFormRequestEntity } from "../../domain/entities/ManageConfigFormRequestEntity";
import { IManageConfigObservedState } from "../interfaces/IManageConfig";

// ─── Input ────────────────────────────────────────────────────────────────────
// Input = command streams only. No state, no loading flags.

class ManageConfigInput implements Disposable {
  public fetchList = new BehaviorSubject<ManageConfigListRequestEntity>({});
  public fetchDetail = new BehaviorSubject<string>("");
  public createManageConfig = new BehaviorSubject<ManageConfigFormRequestEntity>({});
  public updateManageConfig = new BehaviorSubject<ManageConfigFormRequestEntity>({});
  public deleteManageConfig = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.createManageConfig.complete();
    this.updateManageConfig.complete();
    this.deleteManageConfig.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────
// Output = one-time events/effects only. No renderable state here.

class ManageConfigOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<"create" | "update" | "delete" | null>(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class ManageConfigViewModel extends BasedObservedViewModel<
  ManageConfigInput,
  ManageConfigOutput,
  IManageConfigObservedState
> {
  constructor() {
    super(new ManageConfigInput(), new ManageConfigOutput());
  }

  private useCase = ManageConfigInjection.getUseCase();

  protected override createObservedState(): IManageConfigObservedState {
    return {
      loading: false,
      formLoading: false,
      list: [],
      detail: {},
      paging: { page: 0, limit: 10, total: 0 },
    };
  }

  public buildTableQuery(
    pagination: any,
    sorter: any,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: any,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    let order = prevParams?.order;
    if (sorter && sorter.field && sorter.order) {
      const direction = sorter.order === "ascend" ? "ASC" : "DESC";
      order = `${sorter.field} ${direction}`;
    }

    const query = {
      ...prevParams,
      ...formValues,
      page: (pagination?.current ?? 1) - 1,
      limit: pagination?.pageSize ?? 10,
      order,
    };

    return { query };
  }

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getManageConfigList(params);
        if (res) {
          this.setObservedState({
            list: res.data || [],
            paging: res.pagination || {
              page: params.page || 0,
              limit: params.limit || 10,
              total: 0,
            },
          });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchDetail, async (code) => {
      if (!code) return;
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getManageConfigDetail(code);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Create
    this.skipFirstInitial(this.input.createManageConfig, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.createManageConfig(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Update
    this.skipFirstInitial(this.input.updateManageConfig, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.updateManageConfig({
          code: body.code,
          title: body.title,
          description: body.description,
          config: body.config,
        });
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Delete
    this.skipFirstInitial(this.input.deleteManageConfig, async (code) => {
      if (!code) return;
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.deleteManageConfig(code);
        if (res) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });
  }
}
